import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  getProjects,
  createProject,
  updateProject,
  deleteProject,
  addProjectStatus,
  deleteProjectStatus,
} from '../../lib/api';
import {
  Plus,
  Trash2,
  Edit2,
  FolderKanban,
  Calendar,
  User,
  Clock,
  Play,
  Pause,
  CheckCircle2,
  AlertCircle,
  MessageSquarePlus,
  X,
  Users,
  ListTodo,
  Circle,
  CheckCircle,
  Loader2,
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'planning', label: 'Planning' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const STATUS_CONFIG = {
  planning: { label: 'Planning', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: Play },
  on_hold: { label: 'On Hold', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Pause },
  completed: { label: 'Completed', color: 'bg-green-500/15 text-green-400 border-green-500/30', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-500/15 text-red-400 border-red-500/30', icon: AlertCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  medium: { label: 'Medium', color: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  high: { label: 'High', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  critical: { label: 'Critical', color: 'bg-red-500/15 text-red-400 border-red-500/30' },
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Not set';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const EMPTY_FORM = {
  name: '',
  description: '',
  status: 'planning',
  priority: 'medium',
  start_date: '',
  target_completion: '',
  project_lead: '',
  team_members: '',
  progress_percent: 0,
};

export const ProjectAdmin = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusProjectId, setStatusProjectId] = useState(null);
  const [statusContent, setStatusContent] = useState('');
  
  // Task-related state
  const [projectTasks, setProjectTasks] = useState([]);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [addingTask, setAddingTask] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = async () => {
    try {
      const res = await getProjects();
      setProjects(res.data?.projects || []);
    } catch {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenCreate = () => {
    setEditingProject(null);
    setFormData(EMPTY_FORM);
    setProjectTasks([]);
    setDialogOpen(true);
  };

  const handleOpenEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'planning',
      priority: project.priority || 'medium',
      start_date: project.start_date || '',
      target_completion: project.target_completion || '',
      project_lead: project.project_lead || '',
      team_members: (project.team_members || []).join(', '),
      progress_percent: project.progress_percent || 0,
    });
    setProjectTasks(project.tasks || []);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        team_members: formData.team_members
          ? formData.team_members.split(',').map(m => m.trim()).filter(Boolean)
          : [],
        progress_percent: parseInt(formData.progress_percent) || 0,
      };

      if (editingProject) {
        await updateProject(editingProject.id, payload);
        toast.success('Project updated');
      } else {
        await createProject(payload);
        toast.success('Project created');
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error('Failed to save project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Are you sure you want to delete this project?')) return;
    try {
      await deleteProject(projectId);
      toast.success('Project deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const handleAddStatus = async () => {
    if (!statusContent.trim()) {
      toast.error('Status update content is required');
      return;
    }
    try {
      await addProjectStatus(statusProjectId, statusContent, user?.email);
      toast.success('Status update added');
      setStatusDialogOpen(false);
      setStatusContent('');
      fetchData();
    } catch {
      toast.error('Failed to add status update');
    }
  };

  const handleDeleteStatus = async (projectId, statusId) => {
    try {
      await deleteProjectStatus(projectId, statusId);
      toast.success('Status update removed');
      fetchData();
    } catch {
      toast.error('Failed to delete status update');
    }
  };

  // Task management functions
  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !editingProject) return;
    
    setAddingTask(true);
    try {
      const response = await fetch(`${API_URL}/api/projects/${editingProject.id}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTaskTitle.trim() })
      });
      
      if (response.ok) {
        const data = await response.json();
        setProjectTasks(prev => [...prev, data.task]);
        setNewTaskTitle('');
        toast.success('Task added');
      } else {
        toast.error('Failed to add task');
      }
    } catch (err) {
      console.error('Failed to add task:', err);
      toast.error('Failed to add task');
    } finally {
      setAddingTask(false);
    }
  };

  const handleToggleTaskStatus = async (task) => {
    if (!editingProject) return;
    setUpdatingTaskId(task.id);
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${editingProject.id}/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        setProjectTasks(prev => prev.map(t => 
          t.id === task.id 
            ? { ...t, status: newStatus, completed_at: newStatus === 'completed' ? new Date().toISOString() : null }
            : t
        ));
      }
    } catch (err) {
      console.error('Failed to update task:', err);
      toast.error('Failed to update task');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!editingProject) return;
    try {
      const response = await fetch(`${API_URL}/api/projects/${editingProject.id}/tasks/${taskId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setProjectTasks(prev => prev.filter(t => t.id !== taskId));
        toast.success('Task deleted');
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
      toast.error('Failed to delete task');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Projects ({projects.length})
          </h2>
          <p className="text-sm text-muted-foreground">Manage project status and timelines</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2" data-testid="create-project-btn">
          <Plus className="w-4 h-4" />
          New Project
        </Button>
      </div>

      {/* Projects List */}
      {projects.length === 0 ? (
        <Card className="bg-card/80 backdrop-blur-sm border-border/50 p-8 text-center">
          <FolderKanban className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">No projects yet</p>
          <p className="text-sm text-muted-foreground mt-1">Click "New Project" to get started</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {projects.map((project) => {
            const status = STATUS_CONFIG[project.status] || STATUS_CONFIG.planning;
            const priority = PRIORITY_CONFIG[project.priority] || PRIORITY_CONFIG.medium;
            const StatusIcon = status.icon;

            return (
              <Card key={project.id} className="bg-card/80 backdrop-blur-sm border-border/50 p-5" data-testid={`admin-project-${project.id}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Top Row */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <h3 className="font-semibold text-base" style={{ fontFamily: 'Chivo, sans-serif' }}>{project.name}</h3>
                        {project.description && (
                          <p className="text-sm text-muted-foreground mt-0.5">{project.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className={cn("text-xs", priority.color)}>{priority.label}</Badge>
                        <Badge variant="outline" className={cn("text-xs gap-1", status.color)}>
                          <StatusIcon className="w-3 h-3" />{status.label}
                        </Badge>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{project.progress_percent}%</span>
                      </div>
                      <Progress value={project.progress_percent} className="h-1.5" />
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap mb-3">
                      {project.project_lead && (
                        <span className="flex items-center gap-1"><User className="w-3 h-3" />{project.project_lead}</span>
                      )}
                      {project.start_date && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Started {formatDate(project.start_date)}</span>
                      )}
                      {project.target_completion && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Due {formatDate(project.target_completion)}</span>
                      )}
                      {project.team_members?.length > 0 && (
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{project.team_members.length} members</span>
                      )}
                    </div>

                    {/* Status Updates */}
                    {project.status_updates?.length > 0 && (
                      <div className="border-t border-border/50 pt-2 space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Latest Updates</p>
                        {project.status_updates.slice(0, 3).map((update) => (
                          <div key={update.id} className="flex items-start gap-2 text-sm group">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                            <div className="flex-1">
                              <p>{update.content}</p>
                              <p className="text-xs text-muted-foreground">
                                {update.created_by && <span>{update.created_by} &middot; </span>}
                                {formatDate(update.created_at)}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity text-destructive"
                              onClick={() => handleDeleteStatus(project.id, update.id)}
                              data-testid={`delete-status-${update.id}`}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setStatusProjectId(project.id); setStatusDialogOpen(true); }}
                      title="Add status update"
                      data-testid={`add-status-btn-${project.id}`}
                    >
                      <MessageSquarePlus className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenEdit(project)}
                      title="Edit project"
                      data-testid={`edit-project-btn-${project.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(project.id)}
                      title="Delete project"
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-project-btn-${project.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editingProject ? 'Edit Project' : 'New Project'}
            </DialogTitle>
            <DialogDescription>
              {editingProject ? 'Update project details and progress' : 'Create a new project to track'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="project-name">Project Name *</Label>
              <Input
                id="project-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Cloud Migration Phase 2"
                className="mt-1.5"
                data-testid="project-name-input"
              />
            </div>

            <div>
              <Label htmlFor="project-desc">Description</Label>
              <Textarea
                id="project-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief project description..."
                className="mt-1.5"
                rows={2}
                data-testid="project-desc-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="project-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="project-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="mt-1.5"
                  data-testid="project-start-date"
                />
              </div>
              <div>
                <Label htmlFor="target-date">Target Completion</Label>
                <Input
                  id="target-date"
                  type="date"
                  value={formData.target_completion}
                  onChange={(e) => setFormData({ ...formData, target_completion: e.target.value })}
                  className="mt-1.5"
                  data-testid="project-target-date"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="project-lead">Project Lead</Label>
              <Input
                id="project-lead"
                value={formData.project_lead}
                onChange={(e) => setFormData({ ...formData, project_lead: e.target.value })}
                placeholder="e.g. John Smith"
                className="mt-1.5"
                data-testid="project-lead-input"
              />
            </div>

            <div>
              <Label htmlFor="team-members">Team Members (comma-separated)</Label>
              <Input
                id="team-members"
                value={formData.team_members}
                onChange={(e) => setFormData({ ...formData, team_members: e.target.value })}
                placeholder="e.g. Alice, Bob, Charlie"
                className="mt-1.5"
                data-testid="project-team-input"
              />
            </div>

            {editingProject && (
              <div>
                <Label htmlFor="progress">Progress: {formData.progress_percent}%</Label>
                <input
                  id="progress"
                  type="range"
                  min="0"
                  max="100"
                  value={formData.progress_percent}
                  onChange={(e) => setFormData({ ...formData, progress_percent: parseInt(e.target.value) })}
                  className="w-full mt-1.5 accent-primary"
                  data-testid="project-progress-slider"
                />
              </div>
            )}

            {/* Tasks Section - Only show when editing */}
            {editingProject && (
              <div className="border-t border-border/50 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <ListTodo className="w-4 h-4" />
                    Tasks ({projectTasks.filter(t => t.status === 'completed').length}/{projectTasks.length})
                  </Label>
                </div>
                
                {/* Task List */}
                {projectTasks.length > 0 && (
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {projectTasks.map((task) => {
                      const isCompleted = task.status === 'completed';
                      const isUpdating = updatingTaskId === task.id;
                      
                      return (
                        <div 
                          key={task.id} 
                          className={`flex items-start gap-2 p-2 rounded-lg transition-colors group ${
                            isCompleted ? 'bg-green-500/10' : 'bg-muted/30 hover:bg-muted/50'
                          }`}
                          data-testid={`admin-task-${task.id}`}
                        >
                          <button
                            onClick={() => handleToggleTaskStatus(task)}
                            disabled={isUpdating}
                            className={`mt-0.5 shrink-0 transition-colors ${
                              isCompleted ? 'text-green-400' : 'text-muted-foreground hover:text-primary'
                            }`}
                            data-testid={`admin-task-toggle-${task.id}`}
                          >
                            {isUpdating ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : isCompleted ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <Circle className="w-4 h-4" />
                            )}
                          </button>
                          <span className={`flex-1 text-sm ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                            {task.title}
                          </span>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                            data-testid={`admin-task-delete-${task.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Add Task Form */}
                <form onSubmit={handleAddTask} className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Add a new task..."
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    className="h-9 text-sm"
                    disabled={addingTask}
                    data-testid="admin-task-input"
                  />
                  <Button 
                    type="submit" 
                    size="sm" 
                    className="h-9 px-3"
                    disabled={addingTask || !newTaskTitle.trim()}
                    data-testid="admin-task-add-btn"
                  >
                    {addingTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </form>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-project-btn">
              {isSaving ? 'Saving...' : (editingProject ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Status Update Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add Status Update</DialogTitle>
            <DialogDescription>Post a status update for this project</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={statusContent}
              onChange={(e) => setStatusContent(e.target.value)}
              placeholder="What's the latest on this project?"
              rows={3}
              data-testid="status-content-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddStatus} data-testid="submit-status-btn">
              Post Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
